import React, { useMemo } from 'react';
import traitsData from '../../../data/traits.json';

interface TraitsTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedRulebookIds: string[];
}

export const TraitsTab: React.FC<TraitsTabProps> = ({
  searchTerm,
  setSearchTerm,
  selectedRulebookIds
}) => {
  interface Trait {
    id: string;
    name: string;
    description: string;
    rulebook: string;
  }

  const traits = traitsData.traits as Trait[];

  const visibleTraits = useMemo(
    () => traits.filter((trait) => selectedRulebookIds.includes(trait.rulebook)),
    [traits, selectedRulebookIds]
  );

  const filteredTraits = useMemo(() => {
    if (!searchTerm) return visibleTraits;
    const term = searchTerm.toLowerCase();
    return visibleTraits.filter(
      (trait) =>
        trait.name.toLowerCase().includes(term) || trait.description.toLowerCase().includes(term)
    );
  }, [searchTerm, visibleTraits]);

  return (
    <div>
      {/* Search */}
      <div className="bg-surface-alt rounded-lg p-4 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-ink mb-1">Search Traits</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or description..."
            className="w-full px-3 py-2 border border-line rounded-md bg-surface text-ink focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-2 text-sm text-ink-dim">
          Showing {filteredTraits.length} of {visibleTraits.length} traits
        </div>
      </div>

      {/* Traits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredTraits.map((trait) => (
          <div
            key={trait.id}
            className="group bg-surface border border-line rounded-lg p-4 hover:shadow-lg transition-shadow"
            title={trait.description}
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-lg font-semibold text-ink">{trait.name}</h3>
              <span className="px-2 py-1 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200">
                {trait.rulebook}
              </span>
            </div>
            <p className="text-ink text-sm">{trait.description}</p>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredTraits.length === 0 && (
        <div className="text-center py-12">
          <p className="text-ink-faint text-lg">No traits found matching your search</p>
        </div>
      )}
    </div>
  );
};
